import { stringify } from 'qs';

import type { ProductListItemType} from '@zoomit/web-kit';
import { ApiManager, SHOW_TOAST_HEADER } from '@zoomit/web-kit';

import { Comparisons, Product, Product_server, ProductReport } from '../../constants/apis';
import type {
  ChartRange,
  PriceChartResponse,
  SingleProduct,
  SingleProductResponse,
} from '../../types/single-product';
import type { ProductStoreForm, PriceAndStore } from '../../types/store';

export const getProduct = async (slug: string) => {
  return await ApiManager.get<SingleProductResponse>(`${Product_server}/${slug}`);
};

export const getProductPreview = async (id: string, version: string, token: string) => {
  return await ApiManager.get<{
    product: SingleProduct;
  }>(`${Product_server}/${id}/${version}/preview`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getStoresByProductId = async (args: ProductStoreForm & { productId: string }) => {
  const { productId, shouldGetAllOnlineStores, shouldGetAllPhysicalStores, ...params } = args;

  return await ApiManager.get<PriceAndStore>(`${Product}/${productId}/stores`, {
    params: {
      size: shouldGetAllOnlineStores ? undefined : 20,
      PhysicalStoresCount: shouldGetAllPhysicalStores ? undefined : 20,
      ...params,
    },
    paramsSerializer: (params) => {
      return stringify(params);
    },
  });
};

export const getPriceChart = async (id: string, timeRange: ChartRange) => {
  const params: any = {};
  if (timeRange !== 'All') {
    params.timeRange = timeRange;
  }
  return await ApiManager.get<PriceChartResponse>(`${Product}/${id}/price-chart`, {
    params,
  });
};

export const getProductsWithSimilarSpecifications = async (id: string) => {
  return await ApiManager.get<ProductListItemType[]>(`${Product}/${id}/related/specification`);
};

export const getMostComparedProducts = async ({ id, size }: { id: string; size?: number }) => {
  const mostComparedUrl = new URL(`${Comparisons}/related/${id}`);
  const params = new URLSearchParams();
  if (size) {
    params.append('size', `${size}`);
  }

  mostComparedUrl.search = params.toString();

  return await ApiManager.get<ProductListItemType[]>(mostComparedUrl.toString());
};

export const getProductsWithSameBrand = async ({
  id,
  size,
  brandId,
  productId,
  categoryId,
}: {
  id: string;
  size?: number;
  brandId: string;
  productId?: string;
  categoryId: string;
}) => {
  const similarBrandUrl = new URL(`${Product}/${id}/related/brand`);
  const params = new URLSearchParams({
    id,
    BrandId: brandId,
    CategoryId: categoryId,
  });
  if (size) {
    params.append('Size', `${size}`);
  }

  if (productId) {
    params.append('ProductId', `${productId}`);
  }

  similarBrandUrl.search = params.toString();

  return await ApiManager.get<ProductListItemType[]>(similarBrandUrl.toString());
};

export const getProductsWithSimilarPrice = async ({
  id,
  size,
  maxPrice,
  minPrice,
  productId,
  categoryId,
}: {
  id: string;
  size?: number;
  minPrice: number;
  maxPrice: number;
  productId?: string;
  categoryId: string;
}) => {
  const similarPriceUrl = new URL(`${Product}/${id}/related/price`);
  const params = new URLSearchParams({
    id,
    MinPrice: `${minPrice}`,
    MaxPrice: `${maxPrice}`,
    CategoryId: categoryId,
  });
  if (size) {
    params.append('Size', `${size}`);
  }

  if (productId) {
    params.append('ProductId', `${productId}`);
  }

  similarPriceUrl.search = params.toString();

  return await ApiManager.get<ProductListItemType[]>(similarPriceUrl.toString());
};

export const reportProduct = async ({
  productId,
  token,
  description,
}: {
  productId: string;
  description: string;
  token: string;
}) => {
  return await ApiManager.post(
    ProductReport,
    { productId, description },
    { headers: { Authorization: `Bearer ${token}` } },
  );
};

export const visitProduct = async (productId: string, session: string) => {
  return await ApiManager.post(`${Product}/${productId}/visit?session=${session}`, {
    headers: { [SHOW_TOAST_HEADER]: false },
  });
};
